/**
 * Lawn Care Cost Database
 * Regional pricing for various lawn care services, materials, and equipment
 */

export interface RegionalCosts {
  region: string
  laborRate: number // $ per hour
  
  // Fertilizer costs (per 1000 sq ft)
  fertilizer: {
    basic: number // Basic NPK fertilizer
    premium: number // Slow-release, premium blend
    organic: number // Organic fertilizer
    starter: number // Starter fertilizer for seeding
    winterizer: number // Fall winterizer
  }
  
  // Seed costs (per 1000 sq ft)
  seed: {
    coolSeason: number // Kentucky Bluegrass, Fescue, Ryegrass
    warmSeason: number // Bermuda, Zoysia, St. Augustine
    premium: number // Premium blends
  }
  
  // Treatment costs (per 1000 sq ft)
  treatments: {
    weedControl: number // Pre/post-emergent herbicide
    pestControl: number // Insecticide application
    diseaseControl: number // Fungicide treatment
    limeApplication: number // pH adjustment
    aeration: number // Core aeration service
    dethatching: number // Power rake/dethatch
    overseeding: number // Overseeding with seed
  }
  
  // Maintenance costs
  maintenance: {
    mowingPerVisit: number // Per mowing (avg size lawn)
    edgingPerVisit: number // Edging service
    waterPerMonth: number // Average water cost
  }
  
  // Equipment costs (if DIY)
  equipment: {
    pushMower: number // Basic push mower
    selfPropelledMower: number // Self-propelled mower
    ridingMower: number // Riding mower
    spreader: number // Broadcast spreader
    aerator: number // Core aerator rental per day
    sprinkler: number // Basic sprinkler system cost per zone
  }
}

export const REGIONAL_LAWN_COSTS: Record<string, RegionalCosts> = {
  'Northeast': {
    region: 'Northeast',
    laborRate: 55,
    fertilizer: {
      basic: 8,
      premium: 15,
      organic: 20,
      starter: 18,
      winterizer: 12
    },
    seed: {
      coolSeason: 12,
      warmSeason: 18,
      premium: 25
    },
    treatments: {
      weedControl: 10,
      pestControl: 12,
      diseaseControl: 15,
      limeApplication: 8,
      aeration: 15,
      dethatching: 12,
      overseeding: 25
    },
    maintenance: {
      mowingPerVisit: 45,
      edgingPerVisit: 15,
      waterPerMonth: 35
    },
    equipment: {
      pushMower: 250,
      selfPropelledMower: 450,
      ridingMower: 2500,
      spreader: 80,
      aerator: 75,
      sprinkler: 150
    }
  },
  
  'Southeast': {
    region: 'Southeast',
    laborRate: 45,
    fertilizer: {
      basic: 7,
      premium: 13,
      organic: 18,
      starter: 16,
      winterizer: 10
    },
    seed: {
      coolSeason: 15,
      warmSeason: 12,
      premium: 22
    },
    treatments: {
      weedControl: 9,
      pestControl: 10,
      diseaseControl: 13,
      limeApplication: 7,
      aeration: 12,
      dethatching: 10,
      overseeding: 22
    },
    maintenance: {
      mowingPerVisit: 40,
      edgingPerVisit: 12,
      waterPerMonth: 45
    },
    equipment: {
      pushMower: 230,
      selfPropelledMower: 420,
      ridingMower: 2300,
      spreader: 75,
      aerator: 70,
      sprinkler: 140
    }
  },
  
  'Midwest': {
    region: 'Midwest',
    laborRate: 48,
    fertilizer: {
      basic: 7,
      premium: 14,
      organic: 19,
      starter: 17,
      winterizer: 11
    },
    seed: {
      coolSeason: 11,
      warmSeason: 16,
      premium: 23
    },
    treatments: {
      weedControl: 9,
      pestControl: 11,
      diseaseControl: 14,
      limeApplication: 7,
      aeration: 13,
      dethatching: 11,
      overseeding: 23
    },
    maintenance: {
      mowingPerVisit: 42,
      edgingPerVisit: 13,
      waterPerMonth: 30
    },
    equipment: {
      pushMower: 240,
      selfPropelledMower: 440,
      ridingMower: 2400,
      spreader: 78,
      aerator: 72,
      sprinkler: 145
    }
  },
  
  'Southwest': {
    region: 'Southwest',
    laborRate: 50,
    fertilizer: {
      basic: 8,
      premium: 16,
      organic: 21,
      starter: 19,
      winterizer: 13
    },
    seed: {
      coolSeason: 14,
      warmSeason: 11,
      premium: 24
    },
    treatments: {
      weedControl: 11,
      pestControl: 13,
      diseaseControl: 16,
      limeApplication: 9,
      aeration: 14,
      dethatching: 12,
      overseeding: 24
    },
    maintenance: {
      mowingPerVisit: 48,
      edgingPerVisit: 14,
      waterPerMonth: 55
    },
    equipment: {
      pushMower: 260,
      selfPropelledMower: 470,
      ridingMower: 2600,
      spreader: 82,
      aerator: 78,
      sprinkler: 160
    }
  },
  
  'West': {
    region: 'West',
    laborRate: 60,
    fertilizer: {
      basic: 9,
      premium: 17,
      organic: 23,
      starter: 20,
      winterizer: 14
    },
    seed: {
      coolSeason: 13,
      warmSeason: 13,
      premium: 26
    },
    treatments: {
      weedControl: 12,
      pestControl: 14,
      diseaseControl: 17,
      limeApplication: 10,
      aeration: 16,
      dethatching: 13,
      overseeding: 26
    },
    maintenance: {
      mowingPerVisit: 52,
      edgingPerVisit: 16,
      waterPerMonth: 50
    },
    equipment: {
      pushMower: 270,
      selfPropelledMower: 490,
      ridingMower: 2700,
      spreader: 85,
      aerator: 80,
      sprinkler: 165
    }
  },
  
  'Pacific Northwest': {
    region: 'Pacific Northwest',
    laborRate: 58,
    fertilizer: {
      basic: 9,
      premium: 16,
      organic: 22,
      starter: 19,
      winterizer: 13
    },
    seed: {
      coolSeason: 12,
      warmSeason: 16,
      premium: 25
    },
    treatments: {
      weedControl: 11,
      pestControl: 13,
      diseaseControl: 16,
      limeApplication: 9,
      aeration: 15,
      dethatching: 12,
      overseeding: 25
    },
    maintenance: {
      mowingPerVisit: 50,
      edgingPerVisit: 15,
      waterPerMonth: 40
    },
    equipment: {
      pushMower: 265,
      selfPropelledMower: 480,
      ridingMower: 2650,
      spreader: 83,
      aerator: 78,
      sprinkler: 158
    }
  },

  'Mid-Atlantic': {
    region: 'Mid-Atlantic',
    laborRate: 52,
    fertilizer: {
      basic: 8,
      premium: 15,
      organic: 20,
      starter: 17,
      winterizer: 12
    },
    seed: {
      coolSeason: 12,
      warmSeason: 15,
      premium: 23
    },
    treatments: {
      weedControl: 10,
      pestControl: 12,
      diseaseControl: 15,
      limeApplication: 8,
      aeration: 14,
      dethatching: 11,
      overseeding: 23
    },
    maintenance: {
      mowingPerVisit: 45,
      edgingPerVisit: 14,
      waterPerMonth: 38
    },
    equipment: {
      pushMower: 245,
      selfPropelledMower: 445,
      ridingMower: 2450,
      spreader: 79,
      aerator: 74,
      sprinkler: 148
    }
  },

  'Great Lakes': {
    region: 'Great Lakes',
    laborRate: 50,
    fertilizer: {
      basic: 8,
      premium: 15,
      organic: 20,
      starter: 17,
      winterizer: 12
    },
    seed: {
      coolSeason: 11,
      warmSeason: 16,
      premium: 24
    },
    treatments: {
      weedControl: 10,
      pestControl: 12,
      diseaseControl: 15,
      limeApplication: 8,
      aeration: 14,
      dethatching: 11,
      overseeding: 24
    },
    maintenance: {
      mowingPerVisit: 44,
      edgingPerVisit: 13,
      waterPerMonth: 32
    },
    equipment: {
      pushMower: 242,
      selfPropelledMower: 442,
      ridingMower: 2420,
      spreader: 78,
      aerator: 73,
      sprinkler: 146
    }
  },

  'Upper Midwest': {
    region: 'Upper Midwest',
    laborRate: 48,
    fertilizer: {
      basic: 7,
      premium: 14,
      organic: 19,
      starter: 16,
      winterizer: 11
    },
    seed: {
      coolSeason: 10,
      warmSeason: 15,
      premium: 22
    },
    treatments: {
      weedControl: 9,
      pestControl: 11,
      diseaseControl: 14,
      limeApplication: 7,
      aeration: 13,
      dethatching: 10,
      overseeding: 22
    },
    maintenance: {
      mowingPerVisit: 42,
      edgingPerVisit: 12,
      waterPerMonth: 28
    },
    equipment: {
      pushMower: 235,
      selfPropelledMower: 435,
      ridingMower: 2350,
      spreader: 76,
      aerator: 71,
      sprinkler: 142
    }
  },

  'Central': {
    region: 'Central',
    laborRate: 46,
    fertilizer: {
      basic: 7,
      premium: 13,
      organic: 18,
      starter: 15,
      winterizer: 10
    },
    seed: {
      coolSeason: 11,
      warmSeason: 13,
      premium: 21
    },
    treatments: {
      weedControl: 9,
      pestControl: 10,
      diseaseControl: 13,
      limeApplication: 7,
      aeration: 12,
      dethatching: 10,
      overseeding: 21
    },
    maintenance: {
      mowingPerVisit: 41,
      edgingPerVisit: 12,
      waterPerMonth: 35
    },
    equipment: {
      pushMower: 235,
      selfPropelledMower: 430,
      ridingMower: 2300,
      spreader: 75,
      aerator: 70,
      sprinkler: 140
    }
  },

  'South Central': {
    region: 'South Central',
    laborRate: 47,
    fertilizer: {
      basic: 7,
      premium: 14,
      organic: 19,
      starter: 16,
      winterizer: 11
    },
    seed: {
      coolSeason: 12,
      warmSeason: 11,
      premium: 22
    },
    treatments: {
      weedControl: 9,
      pestControl: 11,
      diseaseControl: 14,
      limeApplication: 8,
      aeration: 13,
      dethatching: 11,
      overseeding: 22
    },
    maintenance: {
      mowingPerVisit: 43,
      edgingPerVisit: 13,
      waterPerMonth: 48
    },
    equipment: {
      pushMower: 240,
      selfPropelledMower: 440,
      ridingMower: 2400,
      spreader: 77,
      aerator: 72,
      sprinkler: 145
    }
  },

  'Mountain West': {
    region: 'Mountain West',
    laborRate: 55,
    fertilizer: {
      basic: 9,
      premium: 17,
      organic: 23,
      starter: 20,
      winterizer: 14
    },
    seed: {
      coolSeason: 13,
      warmSeason: 14,
      premium: 26
    },
    treatments: {
      weedControl: 12,
      pestControl: 14,
      diseaseControl: 17,
      limeApplication: 10,
      aeration: 16,
      dethatching: 13,
      overseeding: 26
    },
    maintenance: {
      mowingPerVisit: 52,
      edgingPerVisit: 16,
      waterPerMonth: 45
    },
    equipment: {
      pushMower: 270,
      selfPropelledMower: 490,
      ridingMower: 2700,
      spreader: 85,
      aerator: 80,
      sprinkler: 165
    }
  },

  'West Coast': {
    region: 'West Coast',
    laborRate: 62,
    fertilizer: {
      basic: 10,
      premium: 18,
      organic: 24,
      starter: 21,
      winterizer: 15
    },
    seed: {
      coolSeason: 14,
      warmSeason: 14,
      premium: 27
    },
    treatments: {
      weedControl: 13,
      pestControl: 15,
      diseaseControl: 18,
      limeApplication: 11,
      aeration: 17,
      dethatching: 14,
      overseeding: 27
    },
    maintenance: {
      mowingPerVisit: 55,
      edgingPerVisit: 17,
      waterPerMonth: 55
    },
    equipment: {
      pushMower: 280,
      selfPropelledMower: 500,
      ridingMower: 2800,
      spreader: 88,
      aerator: 83,
      sprinkler: 170
    }
  }
}

export interface ServiceFrequency {
  name: string
  timesPerYear: number
  months: string[]
}

export const SERVICE_FREQUENCIES = {
  fertilization: {
    light: { name: 'Light (2x/year)', timesPerYear: 2, months: ['April', 'September'] },
    moderate: { name: 'Moderate (3-4x/year)', timesPerYear: 3.5, months: ['April', 'June', 'September', 'November'] },
    heavy: { name: 'Heavy (4-5x/year)', timesPerYear: 4.5, months: ['March', 'May', 'July', 'September', 'November'] }
  },
  mowing: {
    coolSeason: { name: 'Cool Season', timesPerYear: 28, months: ['Apr-Oct (weekly)'] },
    warmSeason: { name: 'Warm Season', timesPerYear: 24, months: ['May-Sep (weekly)'] }
  },
  treatments: {
    weedControl: { name: 'Weed Control', timesPerYear: 2, months: ['Spring', 'Fall'] },
    pestControl: { name: 'Pest Control', timesPerYear: 2, months: ['Summer'] },
    diseaseControl: { name: 'Disease Control', timesPerYear: 1, months: ['As needed'] }
  },
  aeration: {
    coolSeason: { name: 'Cool Season Aeration', timesPerYear: 1, months: ['Fall'] },
    warmSeason: { name: 'Warm Season Aeration', timesPerYear: 1, months: ['Spring'] }
  }
}
